import { usePage } from "@inertiajs/react"
import { useState } from "react"

import FoldRow from "../../../shared/FoldRow"
import Layout from "../../../shared/Layout"
import type { SharedData } from "../../../shared/shared"
import { useTripClock } from "../../../shared/tripClock"

interface FlightCard {
  id: number
  // t022 (FR-5.1, FR-5.2, FR-5.4, FR-6.1): every passenger's name,
  // joined by ", " and name-ordered server-side
  // (`Flights::Flight#passenger_names_text`) -- `null`, not `""`, when
  // the flight has no passengers at all, so this page renders no name
  // line rather than an empty one.
  passengerNames: string | null
  flightNumber: string
  // FR-4.5: "08:15 → 13:40", composed server-side (Flights::Flight
  // #times_text) -- this page formats no date or time of its own.
  timesText: string
  // FR-4.7: "(15.09)" when the arrival date differs from the departure
  // date, null when the two dates are equal -- rendered as its own
  // lighter span immediately after timesText, never joined into it.
  arrivalDateText: string | null
  // FR-4.5: "Прага → Батуми", the same composition as timesText.
  routeText: string
  // FR-4.6, FR-8.2, NFR-5: admin-entered text, rendered verbatim, never
  // through I18n; null when the flight carries no note.
  note: string | null
  // t021 `impl-flights-page` (FR-1, FR-2): the flight's own ISO arrival
  // date -- the sole input to this page's own past-ness predicate
  // (`isPast`, below). Past-ness is judged on arrival, never departure,
  // so a night flight stays plain while still in the air past midnight.
  // Not shared with the main page's own predicate (FR-13).
  arrivalDate: string
}

interface FlightSection {
  heading: string
  // t021 `impl-flights-page` (FR-10, FR-16): the section's own day-
  // numeral, month abbreviation and ISO departure date -- read by the
  // shared fold row's range composer once a leading run of these
  // sections folds.
  dayOfMonth: string
  monthAbbr: string
  date: string
  cards: FlightCard[]
}

interface FlightsProps {
  sections: FlightSection[]
  emptyText: string
  // t021 `impl-flights-page` (FR-10, FR-18): the shared fold row's own
  // two labels -- this page's own pair, not the main page's
  // `pastDays`/`hidePast` (FR-10 bars a shared key).
  labels: { pastFlights: string; hidePast: string }
}

interface FlightCardRowProps {
  card: FlightCard
  past: boolean
}

// One flight card: normal ink, or the past-ink step-down design Blocks
// 5-6 draw (FR-3) -- same anatomy throughout, still no interactive
// element of its own (FR-4.8). Extracted once the per-field ink picking
// made the parent's JSX too deep to read inline (the Days.tsx
// `DayCardRow` precedent).
function FlightCardRow({ card, past }: FlightCardRowProps) {
  return (
    <li className={`rounded-xl border p-3.5 ${past ? "border-gray-100" : "border-gray-200"}`}>
      <div className="flex items-baseline justify-between gap-3">
        <p
          className={`text-[15px] font-semibold leading-[20px] tabular-nums ${
            past ? "text-gray-400" : "text-gray-900"
          }`}
        >
          {card.timesText}
          {card.arrivalDateText !== null && (
            <span className={`font-normal ${past ? "text-gray-300" : "text-gray-500"}`}>
              {" "}
              {card.arrivalDateText}
            </span>
          )}
        </p>
        <p className={`text-[13px] leading-[18px] ${past ? "text-gray-300" : "text-gray-500"}`}>
          {card.flightNumber}
        </p>
      </div>
      {card.passengerNames !== null && (
        <p className={`mt-0.5 text-[13px] leading-[18px] ${past ? "text-gray-500" : "text-gray-900"}`}>
          {card.passengerNames}
        </p>
      )}
      <p className={`mt-0.5 text-[13px] leading-[18px] ${past ? "text-gray-300" : "text-gray-500"}`}>
        {card.routeText}
      </p>
      {card.note !== null && (
        <p className={`mt-0.5 text-[13px] leading-[18px] ${past ? "text-gray-300" : "text-gray-500"}`}>
          {card.note}
        </p>
      )}
    </li>
  )
}

// The public, read-only «Рейсы» page — the third public tab's own page
// (Flights::FlightsController#index renders it). Props mirror exactly
// what the controller sends: every card's times/route text already
// composed server-side (FR-4.5, no date/time formatting here), and the
// note rendered verbatim (FR-4.6, FR-8.2, NFR-5) -- no
// dangerouslySetInnerHTML anywhere. Implements docs/design/flights.html's
// Block 1 verbatim: the section list (FR-4.3, FR-4.4, computed
// server-side by Flights::FlightsController) or, with none at all, the
// single empty-state line (FR-4.9) -- no section chrome in that state.
// Cards carry no link, anchor or onClick anywhere (FR-4.8) -- the page's
// one interactive element is the fold row below.
//
// t021 `impl-flights-page` (FR-1 to FR-5, FR-12): past-ness is decided
// here, on the device, from the trip clock's own `today` (`useTripClock`,
// read through the shared `tripTimezone` prop) compared against each
// card's own ISO `arrivalDate` -- re-deriving on every clock tick, with no
// server-sent past flag (FR-2). A section whose every card is past greys
// its own heading; a mixed section (some past, some not) stays in normal
// ink with only its past cards stepped down. No «Сегодня» treatment
// anywhere (FR-12) -- this stays a reference page, never an accent
// surface.
//
// t021 `impl-flights-page` (FR-6 to FR-9, FR-21b): only the leading run
// of fully-past sections folds (FR-7) -- a fully-past section that
// follows a not-fully-past one stays in its own place, greyed in place,
// never folded, and never counted in the row's range. The row shows only
// mid-trip (FR-8: the run is non-empty and at least one section lies
// outside it) and renders as a direct child of `<main>`, above the first
// remaining section (FR-8b) -- collapsed, folded sections are unmounted
// entirely (FR-6), not merely hidden; expanded, they mount back in
// between the row and that first remaining section, in their own
// chronological order. `open` is local state only (FR-9), untouched by a
// clock tick even when the tick changes what is folded.
export default function Flights({ sections, emptyText, labels }: FlightsProps) {
  const {
    props: { tripTimezone },
  } = usePage<SharedData>()
  const { today } = useTripClock(tripTimezone)
  const [open, setOpen] = useState(false)

  const isPast = (card: FlightCard) => card.arrivalDate < today
  const fullyPast = (section: FlightSection) => section.cards.every(isPast)

  let runLength = 0
  while (runLength < sections.length && fullyPast(sections[runLength])) {
    runLength += 1
  }
  const showFold = runLength > 0 && runLength < sections.length
  const visibleSections = showFold && !open ? sections.slice(runLength) : sections

  return (
    <Layout mainClassName="flex flex-col gap-5 px-4 py-4">
      {sections.length > 0 ? (
        <>
          {showFold && (
            <FoldRow
              open={open}
              onToggle={() => setOpen((value) => !value)}
              dates={sections.slice(0, runLength)}
              labels={{ collapsed: labels.pastFlights, expanded: labels.hidePast }}
            />
          )}
          {visibleSections.map((section) => (
            <section key={section.heading} className="flex flex-col gap-2.5">
              <h2
                className={`text-[13px] font-semibold leading-[18px] ${
                  fullyPast(section) ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {section.heading}
              </h2>
              <ul className="flex flex-col gap-2.5">
                {section.cards.map((card) => (
                  <FlightCardRow key={card.id} card={card} past={isPast(card)} />
                ))}
              </ul>
            </section>
          ))}
        </>
      ) : (
        <p className="py-10 text-center text-sm text-gray-400">{emptyText}</p>
      )}
    </Layout>
  )
}
